import { useEffect, useMemo, useRef, useState } from 'react';
import { nameOnChange } from '@/utils/validation';
import { useFormContext } from 'react-hook-form';
import { Input, Select, SearchableSelect } from '@/components/common';
import { useCountryLookup, useStateLookup } from '@/hooks/useGeography';
import { usePincodeLookup } from '@/hooks/usePincodeLookup';
import { GENDER_OPTIONS, RESIDENCE_STATUS_OPTIONS, calculateAge, type LifeQuoteFormData } from '../../schema';
import { YesNoToggle } from './YesNoToggle';

const PIN_CODE_REGEX = /^\d{6}$/;

// This platform is India-only, but the seeded/live State table also carries
// two unrelated Brazil QA rows (see `components/forms/AddressFields.tsx`'s
// `useIndiaStates`, added in Wave 2 for the same bug on the plain-text
// address forms). That hook is not exported and AddressFields.tsx is not
// this lane's file to change, so this mirrors its exact filter -- fails OPEN
// to the unfiltered list whenever the country lookup hasn't resolved,
// errored, or came back without an India row, rather than duplicating a
// bug-prone reimplementation. See action_items_outside_files for exporting
// `useIndiaStates` so this duplication can be removed later.
const INDIA_COUNTRY_CODE = 'IND';

function todayISO(): string {
  const d = new Date();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${month}-${day}`;
}

export function BasicDetailsStep() {
  const {
    register,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<LifeQuoteFormData>();

  const firstName = watch('first_name');
  const middleName = watch('middle_name');
  const lastName = watch('last_name');
  const gender = watch('gender');
  const dob = watch('date_of_birth');
  const sameAsProposer = watch('life_assured_same_as_proposer');
  const laDob = watch('life_assured_date_of_birth');
  const proposerStateId = watch('proposer_state_id');
  const salesPersonStateId = watch('sales_person_state_id');
  const residenceStatus = watch('residence_status');

  const age = useMemo(() => calculateAge(dob), [dob]);
  const lifeAssuredAge = useMemo(() => calculateAge(laDob), [laDob]);

  // Age is derived, read-only, and updates live as the DOB changes.
  useEffect(() => {
    setValue('age', age, { shouldValidate: false });
  }, [age, setValue]);

  // "Life Assured same as Proposer = Yes" auto-copies the proposer identity
  // fields into the life_assured_* fields live, so Review/submit always has a
  // consistent Life Assured identity without the user re-entering anything.
  useEffect(() => {
    if (sameAsProposer !== 'yes') return;
    setValue('life_assured_first_name', firstName, { shouldValidate: false });
    setValue('life_assured_middle_name', middleName, { shouldValidate: false });
    setValue('life_assured_last_name', lastName, { shouldValidate: false });
    setValue('life_assured_gender', gender, { shouldValidate: false });
    setValue('life_assured_date_of_birth', dob, { shouldValidate: false });
    setValue('life_assured_age', age, { shouldValidate: false });
  }, [sameAsProposer, firstName, middleName, lastName, gender, dob, age, setValue]);

  // When "No", the sub-section's own DOB drives its own auto-calculated age.
  useEffect(() => {
    if (sameAsProposer !== 'no') return;
    setValue('life_assured_age', lifeAssuredAge, { shouldValidate: false });
  }, [sameAsProposer, lifeAssuredAge, setValue]);

  const { data: statesRaw } = useStateLookup();
  const { data: countries } = useCountryLookup();
  const indiaCountryId = useMemo(() => {
    const list = Array.isArray(countries) ? countries : [];
    return list.find((c) => c.code === INDIA_COUNTRY_CODE)?.id;
  }, [countries]);
  // 'Proposer State' and 'Sales Person State' both listed Brazil's two QA
  // states alongside India's -- filtered out here, client-side, exactly as
  // AddressFields' useIndiaStates does. Stored value shape is unchanged:
  // still `state.id`.
  const states = useMemo(() => {
    const list = Array.isArray(statesRaw) ? statesRaw : [];
    if (!indiaCountryId) return list;
    const indiaOnly = list.filter((s) => s.country === indiaCountryId);
    return indiaOnly.length > 0 ? indiaOnly : list;
  }, [statesRaw, indiaCountryId]);
  const stateOptions = useMemo(
    () => states.map((s) => ({ value: s.id, label: s.code ? `${s.name} (${s.code})` : s.name })),
    [states]
  );

  // B6: PIN pre-fills Proposer State. Proposer State is already a proper
  // dropdown (SearchableSelect over the real states lookup, keyed by id, not
  // free text), so this only ADDS the auto-fill -- it does not need
  // AddressFields/StateSelect the way the plain-text forms do. Sales Person
  // State is a different person's state (the servicing agent's), not the
  // proposer's address, so a PIN code has no bearing on it and it is left
  // untouched here.
  const { lookup: lookupPincode, loading: pincodeLookupLoading } = usePincodeLookup();
  const [pincodeHint, setPincodeHint] = useState<string | null>(null);
  const lastResolvedPincode = useRef<string | null>(null);

  const handlePincodeBlur = async (raw: string) => {
    const code = raw.trim();
    if (!PIN_CODE_REGEX.test(code) || code === lastResolvedPincode.current) {
      return;
    }
    lastResolvedPincode.current = code;
    const outcome = await lookupPincode(code);
    if (outcome.status === 'found') {
      const matchedState = (states ?? []).find(
        (s) => s.name.toLowerCase() === outcome.stateName.toLowerCase()
      );
      if (matchedState) {
        const picked = stateOptions.find((o) => o.value === matchedState.id);
        setValue('proposer_state_id', matchedState.id, { shouldValidate: true, shouldDirty: true });
        setValue('proposer_state_name', picked?.label ?? matchedState.name, { shouldValidate: false });
        setPincodeHint(null);
        return;
      }
    }
    setPincodeHint('Could not auto-fill Proposer State for this PIN code — please choose it manually.');
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Basic Details</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 mb-6">
          Tell us about the proposer and the life to be insured.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Input label="First Name" required error={errors.first_name?.message} {...register('first_name', { onChange: nameOnChange })} />
          <Input label="Middle Name" error={errors.middle_name?.message} {...register('middle_name')} />
          <Input label="Last Name" required error={errors.last_name?.message} {...register('last_name', { onChange: nameOnChange })} />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
          <Select
            label="Gender"
            options={GENDER_OPTIONS}
            placeholder="Select gender"
            required
            error={errors.gender?.message}
            {...register('gender')}
          />
          <Input
            label="Date of Birth"
            type="date"
            max={todayISO()}
            required
            error={errors.date_of_birth?.message}
            {...register('date_of_birth')}
          />
          <Input label="Age" value={age !== undefined ? `${age} years` : ''} readOnly disabled helperText="Auto-calculated from date of birth" />
        </div>
      </div>

      <div className="border-t border-gray-100 dark:border-gray-700 pt-6">
        <YesNoToggle
          label="Life Assured same as Proposer"
          required
          value={sameAsProposer}
          onChange={(v) => setValue('life_assured_same_as_proposer', v, { shouldValidate: true, shouldDirty: true })}
          error={errors.life_assured_same_as_proposer?.message}
        />

        {sameAsProposer === 'no' && (
          <div className="mt-6">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">Life Assured Details</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Input
                label="First Name"
                required
                error={errors.life_assured_first_name?.message}
                {...register('life_assured_first_name')}
              />
              <Input
                label="Middle Name"
                error={errors.life_assured_middle_name?.message}
                {...register('life_assured_middle_name')}
              />
              <Input
                label="Last Name"
                required
                error={errors.life_assured_last_name?.message}
                {...register('life_assured_last_name')}
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
              <Select
                label="Gender"
                options={GENDER_OPTIONS}
                placeholder="Select gender"
                required
                error={errors.life_assured_gender?.message}
                {...register('life_assured_gender')}
              />
              <Input
                label="Date of Birth"
                type="date"
                max={todayISO()}
                required
                error={errors.life_assured_date_of_birth?.message}
                {...register('life_assured_date_of_birth')}
              />
              <Input
                label="Age"
                value={lifeAssuredAge !== undefined ? `${lifeAssuredAge} years` : ''}
                readOnly
                disabled
                helperText="Auto-calculated from date of birth"
              />
            </div>
          </div>
        )}
      </div>

      <div className="border-t border-gray-100 dark:border-gray-700 pt-6">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">Location &amp; Servicing</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <SearchableSelect
            label="Residence Status"
            required
            value={residenceStatus || ''}
            onChange={(v) => setValue('residence_status', v, { shouldValidate: true, shouldDirty: true })}
            options={RESIDENCE_STATUS_OPTIONS}
            placeholder="Select residence status"
            error={errors.residence_status?.message}
          />
          <Input
            label="PIN Code"
            required
            maxLength={6}
            placeholder="e.g. 400001"
            error={errors.pincode?.message}
            helperText={pincodeLookupLoading ? 'Looking up Proposer State…' : (pincodeHint ?? undefined)}
            {...register('pincode', {
              onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
                void handlePincodeBlur(e.target.value);
              },
            })}
          />
          <SearchableSelect
            label="Proposer State"
            required
            value={proposerStateId || ''}
            onChange={(v) => {
              const picked = stateOptions.find((o) => o.value === v);
              setValue('proposer_state_id', v, { shouldValidate: true, shouldDirty: true });
              setValue('proposer_state_name', picked?.label ?? '', { shouldValidate: false });
            }}
            options={stateOptions}
            placeholder="Select proposer state"
            error={errors.proposer_state_id?.message}
          />
          <SearchableSelect
            label="Sales Person State"
            required
            value={salesPersonStateId || ''}
            onChange={(v) => {
              const picked = stateOptions.find((o) => o.value === v);
              setValue('sales_person_state_id', v, { shouldValidate: true, shouldDirty: true });
              setValue('sales_person_state_name', picked?.label ?? '', { shouldValidate: false });
            }}
            options={stateOptions}
            placeholder="Select sales person state"
            error={errors.sales_person_state_id?.message}
          />
        </div>
      </div>
    </div>
  );
}
